import React, { useState, useEffect } from 'react'

import {
  Box,
  Typography,
  Modal,
  Backdrop,
  Card,
  makeStyles,
  Divider,
  Button,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import useBundlesModal from './useBundlesModal'

import { BundleType } from '../../model/bundles.model'
import AddBundleForm from '../AddBundleForm/AddBundle'
import SelectedBundles from '../SelectedBundles/SelectedBundles'

import DisplayPrices from '~/prices/components/DisplayPrices'

const useStyles = makeStyles(() => ({
  modal: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  card: {
    width: '936px',
  },
  icon: {
    cursor: 'pointer',
  },
  root: {
    '& .MuiFormControl-root': {
      width: '100%',
    },
  },
}))

export default function BundlesModal({ id, type }: { id: string; type: BundleType }) {
  const {
    isOpen,
    setIsOpen,
    tariffName,
    formattedTariffPrice,
    isDisabled,
    canAddBundle,
    selectedBundles,
    cancelBundlesSet,
    bundlesAndTariffPrice,
  } = useBundlesModal(id, type)

  const [addBundle, setAddBundle] = useState(false)

  const [hasContinued, setHasContinued] = useState(false)

  const { modal, card, icon, root } = useStyles()

  const isBonus = type === BundleType.BONUS

  useEffect(() => {
    if (!selectedBundles.length) {
      setAddBundle(true)
    }
  }, [selectedBundles])

  return (
    <>
      <Box display="flex" alignItems="center">
        <Typography
          data-hook="open-bundle-modal"
          onClick={() => setIsOpen(!isDisabled)}
          className={isDisabled ? 'link-disabled' : 'link'}
        >
          Añadir {isBonus ? 'Bono' : 'Descuentos'}
        </Typography>
      </Box>
      <Modal
        open={isOpen}
        BackdropComponent={Backdrop}
        className={modal}
        disableAutoFocus
        disableEnforceFocus
      >
        <Box component={Card} borderRadius="14px" className={card} p={8}>
          <Box display="flex" flexDirection="row" justifyContent="space-between">
            <Box>
              <Typography variant="h2" data-hook="bundles-title">
                Añadir {isBonus ? 'Bono' : 'Descuentos'}
              </Typography>
            </Box>
            <Box>
              <CloseIcon
                data-hook="close-legal"
                onClick={() => {
                  setHasContinued(false)
                  setIsOpen(false)
                  cancelBundlesSet()
                }}
                className={icon}
              />
            </Box>
          </Box>
          <Box py={5}>
            <Divider />
          </Box>
          <Box display="flex" alignItems="center">
            <Box pr={1}>
              <Typography color="textSecondary" variant="body2">
                Tarifa actual:
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2">{tariffName}</Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" mt={4}>
            <Box pr={1}>
              <Typography color="textSecondary" variant="body2">
                Cuota actual:
              </Typography>
            </Box>
            <DisplayPrices prices={formattedTariffPrice} />
          </Box>
          {hasContinued ? (
            <>
              <Box mt={9}>
                <Box mb={4}>
                  <Typography variant="subtitle2">
                    {isBonus ? 'BONOS' : 'DESCUENTOS'} SELECCIONADOS
                  </Typography>
                </Box>
                <SelectedBundles id={id} type={type} dataHook="bundles-summary" />
              </Box>
              {canAddBundle && (
                <Box>
                  <Typography
                    className="link"
                    data-hook="add-another-bundle-btn"
                    onClick={() => {
                      setAddBundle(true)
                      setHasContinued(false)
                    }}
                  >
                    Añadir otro {isBonus ? 'Bono' : 'Descuento'}
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <>
              <Box mt={9} className={root}>
                <Box mb={4}>
                  <Typography variant="subtitle2">
                    SELECCIONA EL {isBonus ? 'BONO' : 'DESCUENTO'}
                  </Typography>
                </Box>
                {selectedBundles.map((bundle) => (
                  <AddBundleForm
                    id={id}
                    type={type}
                    key={`select-bundle-${bundle}`}
                    dataHook={`select-bundle-${bundle}`}
                    preselected={bundle}
                    setAddBundle={setAddBundle}
                  />
                ))}
                {addBundle && (
                  <AddBundleForm
                    dataHook="add-new-bundle"
                    type={type}
                    id={id}
                    setAddBundle={setAddBundle}
                  />
                )}
              </Box>
              <Box>
                <Typography
                  data-hook="add-bundle-btn"
                  onClick={() => setAddBundle(canAddBundle)}
                  className={canAddBundle ? 'link' : 'link-disabled'}
                >
                  Añadir {isBonus ? 'Bono' : 'Descuento'}
                </Typography>
              </Box>
            </>
          )}
          <Box display="flex" alignItems="center" mt={6}>
            <Box pr={1}>
              <Typography color="textSecondary" variant="body2">
                Precio resultante:
              </Typography>
            </Box>
            <Box>
              <DisplayPrices prices={bundlesAndTariffPrice} />
            </Box>
          </Box>
          {hasContinued && (
            <Box mt={8}>
              <Box mb={4}>
                <Typography variant="subtitle2">CÓMO FUNCIONA:</Typography>
              </Box>
              <Box>
                <Typography variant="body1" color="textSecondary">
                  - Lo puede consumir <strong>desde el día que lo activa</strong> hasta el último
                  día de ese mes.
                </Typography>
              </Box>
              <Box mt={4}>
                <Typography variant="body1" color="textSecondary">
                  - La velocidad para el Bono será de <strong>Alta Velocidad hasta 4G</strong> , una
                  vez que lo consuma irá hasta máximo 128 Kbps y no se cobrará el consumo adicional.
                </Typography>
              </Box>
              <Box mt={4}>
                <Typography variant="body1" color="textSecondary">
                  - Se puede dar de <strong>baja gratis</strong> cuando quiera, bien desde el área
                  de cliente o bien llamando al 1707.
                </Typography>
              </Box>
              <Box mt={4}>
                <Typography variant="body1" color="textSecondary">
                  - Una vez dado de baja podrá seguir disfrutando del bono
                  <strong> hasta el final de ese mes</strong>.
                </Typography>
              </Box>
              <Box mt={4}>
                <Typography variant="body1" color="textSecondary">
                  - El coste se añadirá a la
                  <strong> factura de este mes</strong>.
                </Typography>
              </Box>
            </Box>
          )}
          <Box display="flex" mt={11}>
            {hasContinued ? (
              <Box mr={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  data-hook="confirm-bundles-btn"
                  onClick={() => {
                    setHasContinued(false)
                    setIsOpen(false)
                  }}
                >
                  Aplicar {isBonus ? 'bonos' : 'descuentos'}
                </Button>
              </Box>
            ) : (
              <Box mr={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  data-hook="bundles-continue-btn"
                  disabled={!selectedBundles.length}
                  onClick={() => {
                    setHasContinued(true)
                  }}
                >
                  Continuar
                </Button>
              </Box>
            )}
            <Box>
              <Button
                variant="outlined"
                color="secondary"
                data-hook="cancel-bundles-btn"
                onClick={() => {
                  setHasContinued(false)
                  setIsOpen(false)
                  cancelBundlesSet()
                }}
              >
                Cancelar
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
